// src/Services/checkoutService.ts
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { storage, app } from '../Firebase/Config';

// ── Initialise Functions SDK ──────────────────────────────────────────────────
const functions = getFunctions(app, 'asia-south1');
// To use the emulator locally, uncomment:
// import { connectFunctionsEmulator } from 'firebase/functions';
// connectFunctionsEmulator(functions, 'localhost', 5001);

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ShippingAddress {
  fullName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phoneNumber: string;
}

export interface OrderFormData {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerWhatsApp: string;
  items: { productId: string; variantId?: string; quantity: string }[];
  subtotal: string;
  taxAmount: string;
  shippingCost: string;
  discountAmount: string;
  status: 'pending';
  paymentStatus: 'pending';
  paymentMethod: 'credit_card' | 'paypal' | 'bank_transfer' | 'cash_on_delivery';
  shippingAddress: ShippingAddress;
  billingAddress: ShippingAddress;
  notes: string;
  trackingNumber: string;
  estimatedDelivery: string;
  allocatedWarehouseId?: string;
}

export interface PayhereParams {
  sandbox: boolean;
  merchant_id: string;
  return_url: string;
  cancel_url: string;
  notify_url: string;
  order_id: string;
  items: string;
  currency: string;
  amount: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  hash: string;
}

export interface InitiatePaymentResult {
  success: boolean;
  /**
   * The ID of the pending order document in `pendingOrders`.
   * This becomes the real order ID in `orders` once PayHere confirms
   * a successful payment via the payhereNotify webhook.
   * The frontend uses this same ID for the order-confirmation page URL.
   */
  pendingOrderId?: string;
  orderNumber?: string;
  payhereParams?: PayhereParams;
  message?: string;
}

// ── Callable function references ──────────────────────────────────────────────

const initiatePaymentFn = httpsCallable<
  Omit<OrderFormData, 'status' | 'paymentStatus' | 'subtotal' | 'trackingNumber' | 'estimatedDelivery' | 'allocatedWarehouseId'>,
  InitiatePaymentResult
>(functions, 'initiatePayment');

const createOrderFn = httpsCallable<
  Omit<OrderFormData, 'status' | 'paymentStatus' | 'subtotal'>,
  { success: boolean; orderId?: string; orderNumber?: string; message?: string }
>(functions, 'createOrder');

const savePaymentProofFn = httpsCallable<
  { orderId: string; url: string; type: 'image' | 'pdf'; fileName: string },
  { success: boolean }
>(functions, 'savePaymentProof');

const deletePaymentProofFn = httpsCallable<
  { orderId: string; downloadUrl: string },
  { success: boolean }
>(functions, 'deletePaymentProof');

const getOrderByIdFn = httpsCallable<
  { orderId: string },
  { success: boolean; order: Record<string, unknown> }
>(functions, 'getOrderById');

// ─────────────────────────────────────────────────────────────────────────────
// CheckoutService
// ─────────────────────────────────────────────────────────────────────────────

export class CheckoutService {

  // ── initiatePayment ───────────────────────────────────────────────────────
  /**
   * Calls the `initiatePayment` Cloud Function which:
   *  1. Validates products & prices server-side
   *  2. Creates a PENDING order in `pendingOrders` (NOT `orders`) in Firestore
   *  3. Returns a hash + all params needed to redirect to PayHere
   *
   * The real order in `orders` is ONLY created by the `payhereNotify` webhook
   * when PayHere confirms a successful payment (status_code = '2').
   * Failed or cancelled payments cause the pending doc to be deleted with
   * no order ever written to `orders`.
   *
   * Use this for credit_card / paypal flows (PayHere gateway).
   */
  static async initiatePayment(
    formData: OrderFormData
  ): Promise<InitiatePaymentResult> {
    try {
      const {
        status: _s,
        paymentStatus: _ps,
        subtotal: _sub,
        trackingNumber: _tn,
        estimatedDelivery: _ed,
        allocatedWarehouseId: _aw,
        ...payload
      } = formData;

      const result = await initiatePaymentFn(payload);
      return result.data;
    } catch (error: unknown) {
      console.error('initiatePayment Cloud Function error:', error);
      if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof (error as { message: unknown }).message === 'string'
      ) {
        return { success: false, message: (error as { message: string }).message };
      }
      return { success: false, message: 'Failed to initiate payment. Please try again.' };
    }
  }

  // ── redirectToPayhere ─────────────────────────────────────────────────────
  /**
   * Builds a hidden HTML form with PayHere params and auto-submits it,
   * causing the browser to POST to PayHere's checkout page.
   *
   * PayHere requires a form POST (not a GET redirect) to pass params securely.
   */
  static redirectToPayhere(params: PayhereParams): void {
    const payhereBaseUrl = params.sandbox
      ? 'https://sandbox.payhere.lk/pay/checkout'
      : 'https://www.payhere.lk/pay/checkout';

    // Remove 'sandbox' from params — it's not a PayHere field
    const { sandbox: _sandbox, ...payhereFields } = params;

    // Build a hidden form and submit it
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = payhereBaseUrl;
    form.style.display = 'none';

    Object.entries(payhereFields).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = String(value);
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }

  // ── createOrder ───────────────────────────────────────────────────────────
  /**
   * Calls the `createOrder` Cloud Function directly.
   * Use ONLY for non-PayHere payment methods:
   *   - bank_transfer (proof uploaded separately)
   *   - cash_on_delivery
   */
  static async createOrder(
    formData: OrderFormData
  ): Promise<{ success: boolean; orderId?: string; orderNumber?: string; message?: string }> {
    try {
      const { status: _s, paymentStatus: _ps, subtotal: _sub, ...payload } = formData;
      const result = await createOrderFn(payload);
      return result.data;
    } catch (error: unknown) {
      console.error('createOrder Cloud Function error:', error);
      if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof (error as { message: unknown }).message === 'string'
      ) {
        return { success: false, message: (error as { message: string }).message };
      }
      return { success: false, message: 'Failed to create order. Please try again.' };
    }
  }

  // ── uploadPaymentProof ────────────────────────────────────────────────────
  /**
   * Uploads a payment proof file directly to Firebase Storage from the client.
   * Call `savePaymentProofToOrder` afterwards to persist metadata.
   *
   * Path: order_payment_proofs/{orderId}/{timestamp}_{sanitisedFileName}
   */
  static async uploadPaymentProof(
    file: File,
    orderId: string,
    onProgress?: (percent: number) => void
  ): Promise<{ url: string; type: 'image' | 'pdf'; fileName: string }> {
    const timestamp = Date.now();
    const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const storagePath = `order_payment_proofs/${orderId}/${timestamp}_${safeFileName}`;
    const storageRef = ref(storage, storagePath);

    return new Promise((resolve, reject) => {
      const uploadTask = uploadBytesResumable(storageRef, file, {
        contentType: file.type,
      });

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          if (onProgress) {
            const percent = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            onProgress(percent);
          }
        },
        (error) => {
          console.error('Payment proof upload error:', error);
          reject(new Error(`Upload failed: ${error.message}`));
        },
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            const type: 'image' | 'pdf' =
              file.type === 'application/pdf' ? 'pdf' : 'image';
            resolve({ url, type, fileName: file.name });
          } catch (err) {
            reject(new Error('Failed to get download URL after upload'));
          }
        }
      );
    });
  }

  // ── deletePaymentProof ────────────────────────────────────────────────────
  /**
   * Calls the `deletePaymentProof` Cloud Function, which removes the file
   * from Storage and clears proof metadata on the order document.
   * Falls back to client-side Storage deletion if the function call fails.
   */
  static async deletePaymentProof(downloadUrl: string, orderId?: string): Promise<void> {
    if (orderId) {
      try {
        await deletePaymentProofFn({ orderId, downloadUrl });
        return;
      } catch (fnError) {
        console.warn('deletePaymentProof Cloud Function failed, falling back to client delete:', fnError);
      }
    }

    try {
      const storageRef = ref(storage, downloadUrl);
      await deleteObject(storageRef);
    } catch (error: unknown) {
      const code = (error as Record<string, unknown>)?.code;
      if (code === 'storage/object-not-found') return;
      console.error('Error deleting payment proof:', error);
      throw new Error(`Failed to delete payment proof`);
    }
  }

  // ── savePaymentProofToOrder ───────────────────────────────────────────────
  /**
   * Calls the `savePaymentProof` Cloud Function to persist proof metadata
   * onto the order document in Firestore.
   * Call this after a successful `uploadPaymentProof`.
   */
  static async savePaymentProofToOrder(
    orderId: string,
    proofData: { url: string; type: 'image' | 'pdf'; fileName: string } | null
  ): Promise<void> {
    if (!proofData) {
      console.warn('savePaymentProofToOrder called with null proofData — skipping');
      return;
    }

    try {
      await savePaymentProofFn({
        orderId,
        url: proofData.url,
        type: proofData.type,
        fileName: proofData.fileName,
      });
    } catch (error) {
      console.error('savePaymentProofToOrder Cloud Function error:', error);
      throw error;
    }
  }

  // ── getOrderById ──────────────────────────────────────────────────────────
  /**
   * Fetches a single confirmed order by its Firestore document ID.
   *
   * For PayHere orders: this will return null / throw until the
   * payhereNotify webhook fires and creates the order in `orders`.
   * The OrderConfirmation page should poll this until it resolves.
   *
   * Error message codes returned in the `not-found` HttpsError:
   *   'ORDER_PENDING_PAYMENT' — order still in pendingOrders, keep polling
   *   'ORDER_NOT_FOUND'       — payment failed/cancelled, or bad ID
   */
  static async getOrderById(
    orderId: string
  ): Promise<{ success: boolean; order?: Record<string, unknown>; message?: string }> {
    try {
      const result = await getOrderByIdFn({ orderId });
      return result.data;
    } catch (error: unknown) {
      console.error('getOrderById Cloud Function error:', error);
      if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof (error as { message: unknown }).message === 'string'
      ) {
        return { success: false, message: (error as { message: string }).message };
      }
      return { success: false, message: 'Failed to fetch order.' };
    }
  }
}

// // // ---new---
// // src/Services/checkoutService.ts
// import {
//   ref,
//   uploadBytesResumable,
//   getDownloadURL,
//   deleteObject,
// } from 'firebase/storage';
// import { getFunctions, httpsCallable } from 'firebase/functions';
// import { storage, app } from '../Firebase/Config';

// // ── Initialise Functions SDK ──────────────────────────────────────────────────
// // Pass your Firebase `app` instance so the SDK knows which project to target.
// // If you want to use the emulator locally, uncomment the connectFunctionsEmulator line.
// const functions = getFunctions(app);
// // import { connectFunctionsEmulator } from 'firebase/functions';
// // connectFunctionsEmulator(functions, 'localhost', 5001);

// // ── Types ─────────────────────────────────────────────────────────────────────

// interface ShippingAddress {
//   fullName: string;
//   addressLine1: string;
//   addressLine2: string;
//   city: string;
//   state: string;
//   postalCode: string;
//   country: string;
//   phoneNumber: string;
// }

// interface OrderFormData {
//   customerName: string;
//   customerEmail: string;
//   customerPhone: string;
//   customerWhatsApp: string;
//   items: { productId: string; variantId?: string; quantity: string }[];
//   subtotal: string;
//   taxAmount: string;
//   shippingCost: string;
//   discountAmount: string;
//   status: 'pending';
//   paymentStatus: 'pending';
//   paymentMethod: 'credit_card' | 'paypal' | 'bank_transfer' | 'cash_on_delivery';
//   shippingAddress: ShippingAddress;
//   billingAddress: ShippingAddress;
//   notes: string;
//   trackingNumber: string;
//   estimatedDelivery: string;
//   allocatedWarehouseId?: string;
// }

// // ── Callable function references ──────────────────────────────────────────────
// const createOrderFn = httpsCallable<
//   Omit<OrderFormData, 'status' | 'paymentStatus' | 'subtotal'>,
//   { success: boolean; orderId?: string; orderNumber?: string; message?: string }
// >(functions, 'createOrder');

// const savePaymentProofFn = httpsCallable<
//   { orderId: string; url: string; type: 'image' | 'pdf'; fileName: string },
//   { success: boolean }
// >(functions, 'savePaymentProof');

// const deletePaymentProofFn = httpsCallable<
//   { orderId: string; downloadUrl: string },
//   { success: boolean }
// >(functions, 'deletePaymentProof');

// const getOrderByIdFn = httpsCallable<
//   { orderId: string },
//   { success: boolean; order: Record<string, unknown> }
// >(functions, 'getOrderById');

// // ─────────────────────────────────────────────────────────────────────────────
// // CheckoutService
// // All data-writing goes through Firebase Cloud Functions.
// // File uploads to Storage remain client-side (direct SDK upload) for efficiency.
// // ─────────────────────────────────────────────────────────────────────────────

// export class CheckoutService {

//   // ── createOrder ───────────────────────────────────────────────────────────
//   /**
//    * Calls the `createOrder` Cloud Function.
//    * The function validates products server-side, recalculates pricing,
//    * and writes the order document to Firestore.
//    */
//   static async createOrder(
//     formData: OrderFormData
//   ): Promise<{ success: boolean; orderId?: string; orderNumber?: string; message?: string }> {
//     try {
//       // Strip client-only fields that the function doesn't expect
//       const { status: _s, paymentStatus: _ps, subtotal: _sub, ...payload } = formData;

//       const result = await createOrderFn(payload);
//       return result.data;
//     } catch (error: unknown) {
//       console.error('createOrder Cloud Function error:', error);

//       // Firebase Functions errors surface as { code, message, details }
//       if (
//         error &&
//         typeof error === 'object' &&
//         'message' in error &&
//         typeof (error as { message: unknown }).message === 'string'
//       ) {
//         return { success: false, message: (error as { message: string }).message };
//       }

//       return { success: false, message: 'Failed to create order. Please try again.' };
//     }
//   }

//   // ── uploadPaymentProof ────────────────────────────────────────────────────
//   /**
//    * Uploads a payment proof file directly to Firebase Storage from the client.
//    * This avoids routing large binary files through Cloud Functions and is the
//    * recommended pattern for file uploads with Firebase.
//    *
//    * After a successful upload call `savePaymentProofToOrder` to persist the
//    * metadata on the order document via a Cloud Function.
//    *
//    * Path: order_payment_proofs/{orderId}/{timestamp}_{sanitisedFileName}
//    */
//   static async uploadPaymentProof(
//     file: File,
//     orderId: string,
//     onProgress?: (percent: number) => void
//   ): Promise<{ url: string; type: 'image' | 'pdf'; fileName: string }> {
//     const timestamp = Date.now();
//     const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
//     const storagePath = `order_payment_proofs/${orderId}/${timestamp}_${safeFileName}`;
//     const storageRef = ref(storage, storagePath);

//     return new Promise((resolve, reject) => {
//       const uploadTask = uploadBytesResumable(storageRef, file, {
//         contentType: file.type,
//       });

//       uploadTask.on(
//         'state_changed',
//         (snapshot) => {
//           if (onProgress) {
//             const percent = Math.round(
//               (snapshot.bytesTransferred / snapshot.totalBytes) * 100
//             );
//             onProgress(percent);
//           }
//         },
//         (error) => {
//           console.error('Payment proof upload error:', error);
//           reject(new Error(`Upload failed: ${error.message}`));
//         },
//         async () => {
//           try {
//             const url = await getDownloadURL(uploadTask.snapshot.ref);
//             const type: 'image' | 'pdf' =
//               file.type === 'application/pdf' ? 'pdf' : 'image';
//             resolve({ url, type, fileName: file.name });
//           } catch (err) {
//             reject(new Error('Failed to get download URL after upload'));
//           }
//         }
//       );
//     });
//   }

//   // ── deletePaymentProof ────────────────────────────────────────────────────
//   /**
//    * Calls the `deletePaymentProof` Cloud Function, which removes the file
//    * from Storage and clears the proof metadata on the order document.
//    *
//    * Falls back to client-side Storage deletion if the function call fails,
//    * so the UI is never left in a broken state.
//    */
//   static async deletePaymentProof(downloadUrl: string, orderId?: string): Promise<void> {
//     // If we have an orderId, prefer the Cloud Function (also clears Firestore metadata)
//     if (orderId) {
//       try {
//         await deletePaymentProofFn({ orderId, downloadUrl });
//         return;
//       } catch (fnError) {
//         console.warn('deletePaymentProof Cloud Function failed, falling back to client delete:', fnError);
//       }
//     }

//     // Fallback: delete directly from Storage
//     try {
//       const storageRef = ref(storage, downloadUrl);
//       await deleteObject(storageRef);
//     } catch (error: unknown) {
//       const code = (error as Record<string, unknown>)?.code;
//       if (code === 'storage/object-not-found') return; // Already gone
//       console.error('Error deleting payment proof:', error);
//       throw new Error(`Failed to delete payment proof`);
//     }
//   }

//   // ── savePaymentProofToOrder ───────────────────────────────────────────────
//   /**
//    * Calls the `savePaymentProof` Cloud Function to persist proof metadata
//    * (URL, type, file name) onto the order document in Firestore.
//    * Call this after a successful `uploadPaymentProof`.
//    */
//   static async savePaymentProofToOrder(
//     orderId: string,
//     proofData: { url: string; type: 'image' | 'pdf'; fileName: string } | null
//   ): Promise<void> {
//     if (!proofData) {
//       console.warn('savePaymentProofToOrder called with null proofData — skipping');
//       return;
//     }

//     try {
//       await savePaymentProofFn({
//         orderId,
//         url: proofData.url,
//         type: proofData.type,
//         fileName: proofData.fileName,
//       });
//     } catch (error) {
//       console.error('savePaymentProofToOrder Cloud Function error:', error);
//       throw error;
//     }
//   }

//   // ── getOrderById ──────────────────────────────────────────────────────────
//   /**
//    * Fetches a single order by its Firestore document ID via Cloud Function.
//    * Useful for the order confirmation page without giving clients direct
//    * Firestore read access.
//    */
//   static async getOrderById(
//     orderId: string
//   ): Promise<{ success: boolean; order?: Record<string, unknown>; message?: string }> {
//     try {
//       const result = await getOrderByIdFn({ orderId });
//       return result.data;
//     } catch (error: unknown) {
//       console.error('getOrderById Cloud Function error:', error);
//       if (
//         error &&
//         typeof error === 'object' &&
//         'message' in error &&
//         typeof (error as { message: unknown }).message === 'string'
//       ) {
//         return { success: false, message: (error as { message: string }).message };
//       }
//       return { success: false, message: 'Failed to fetch order.' };
//     }
//   }
// }


// // // ---new---


// // // src/Services/checkoutService.ts
// // import {
// //   collection,
// //   addDoc,
// //   serverTimestamp,
// //   Timestamp,
// //   doc,
// //   getDoc,
// //   updateDoc,
// // } from 'firebase/firestore';
// // import {
// //   ref,
// //   uploadBytesResumable,
// //   getDownloadURL,
// //   deleteObject,
// // } from 'firebase/storage';
// // import { db, storage } from '../Firebase/Config';

// // const ORDERS_COLLECTION = 'orders';

// // interface ShippingAddress {
// //   fullName: string;
// //   addressLine1: string;
// //   addressLine2: string;
// //   city: string;
// //   state: string;
// //   postalCode: string;
// //   country: string;
// //   phoneNumber: string;
// // }

// // interface OrderFormData {
// //   customerName: string;
// //   customerEmail: string;
// //   customerPhone: string;
// //   customerWhatsApp: string;
// //   items: { productId: string; variantId?: string; quantity: string }[];
// //   subtotal: string;
// //   taxAmount: string;
// //   shippingCost: string;
// //   discountAmount: string;
// //   status: 'pending';
// //   paymentStatus: 'pending';
// //   paymentMethod: 'credit_card' | 'paypal' | 'bank_transfer' | 'cash_on_delivery';
// //   shippingAddress: ShippingAddress;
// //   billingAddress: ShippingAddress;
// //   notes: string;
// //   trackingNumber: string;
// //   estimatedDelivery: string;
// //   allocatedWarehouseId?: string;
// // }

// // interface VariantSelection {
// //   variantType: string;
// //   variantValue: string;
// // }

// // interface OrderItem {
// //   productId: string;
// //   productName: string;
// //   productSku: string;
// //   productImage?: string;
// //   quantity: number;
// //   // unitPrice: original list price (before discount) — kept for invoicing/reference
// //   unitPrice: number;
// //   // discountedUnitPrice: price actually charged per unit
// //   discountedUnitPrice: number;
// //   // discount percentage applied (0 if none)
// //   discountPercentage: number;
// //   // totalPrice = discountedUnitPrice * quantity  (what the customer pays for this line)
// //   totalPrice: number;
// //   variantId?: string;
// //   variantSelections?: VariantSelection[];
// //   variantSku?: string;
// // }

// // // ── Helper: mirrors productService.calculateDiscountedPrice ──────────────────
// // const calculateDiscountedPrice = (price: number, discount?: number): number => {
// //   if (!discount || discount <= 0 || discount > 100) return price;
// //   return price - (price * discount) / 100;
// // };

// // // Helper: clean data for Firestore (undefined → null)
// // const cleanForFirestore = (data: any): any => {
// //   if (data === null) return null;
// //   if (data === undefined) return null;

// //   if (Array.isArray(data)) {
// //     return data.map(item => cleanForFirestore(item));
// //   }
// //   if (data instanceof Date) {
// //     return Timestamp.fromDate(data);
// //   }
// //   if (data instanceof Timestamp) {
// //     return data;
// //   }
// //   if (typeof data === 'object') {
// //     const cleaned: any = {};
// //     for (const [key, value] of Object.entries(data)) {
// //       cleaned[key] = value !== undefined ? cleanForFirestore(value) : null;
// //     }
// //     return cleaned;
// //   }
// //   return data;
// // };

// // export class CheckoutService {

// //   static async generateOrderNumber(): Promise<string> {
// //     const timestamp = Date.now();
// //     const randomSuffix = Math.random().toString(36).substring(2, 5).toUpperCase();
// //     return `ORD-${timestamp}-${randomSuffix}`;
// //   }

// //   static async fetchProductById(productId: string): Promise<any | null> {
// //     try {
// //       const productRef = doc(db, 'products', productId);
// //       const productSnap = await getDoc(productRef);
// //       if (productSnap.exists()) {
// //         return { id: productSnap.id, ...productSnap.data() };
// //       }
// //       return null;
// //     } catch (error) {
// //       console.error('Error fetching product:', error);
// //       throw error;
// //     }
// //   }

// //   // ── validateOrderItems ────────────────────────────────────────────────────
// //   static async validateOrderItems(
// //     items: { productId: string; variantId?: string; quantity: string }[]
// //   ): Promise<{ orderItems: OrderItem[]; totalDiscountAmount: number }> {
// //     const orderItems: OrderItem[] = [];
// //     let totalDiscountAmount = 0;

// //     for (const item of items) {
// //       if (!item.productId || !item.quantity || parseInt(item.quantity) <= 0) {
// //         throw new Error('Invalid item data');
// //       }

// //       const product = await this.fetchProductById(item.productId);
// //       if (!product) {
// //         throw new Error(`Product not found: ${item.productId}`);
// //       }

// //       let selectedVariant: any = null;
// //       let originalUnitPrice: number = product.price;
// //       let discountPercentage: number = product.discount || 0;
// //       let sku: string = product.sku;
// //       let variantSelections: VariantSelection[] = [];

// //       if (product.hasVariants && product.variants && product.variants.length > 0) {
// //         if (!item.variantId) {
// //           throw new Error(`Variant selection required for product: ${product.name}`);
// //         }

// //         selectedVariant = product.variants.find(
// //           (v: any) => v.id === item.variantId || v.sku === item.variantId
// //         );

// //         if (!selectedVariant) {
// //           throw new Error(`Variant not found for product: ${product.name}`);
// //         }

// //         originalUnitPrice = selectedVariant.price;
// //         discountPercentage =
// //           selectedVariant.discount != null && selectedVariant.discount !== undefined
// //             ? selectedVariant.discount
// //             : product.discount || 0;
// //         sku = selectedVariant.sku;
// //         variantSelections = selectedVariant.variantSelections || [];
// //       }

// //       const quantity = parseInt(item.quantity);
// //       const discountedUnitPrice = calculateDiscountedPrice(originalUnitPrice, discountPercentage);
// //       const totalPrice = discountedUnitPrice * quantity;

// //       totalDiscountAmount += (originalUnitPrice - discountedUnitPrice) * quantity;

// //       const productImage =
// //         product.images && product.images.length > 0 ? product.images[0] : undefined;

// //       orderItems.push({
// //         productId: product.id!,
// //         productName: product.name,
// //         productSku: sku,
// //         productImage,
// //         quantity,
// //         unitPrice: originalUnitPrice,
// //         discountedUnitPrice,
// //         discountPercentage,
// //         totalPrice,
// //         variantId: selectedVariant?.id || undefined,
// //         variantSelections: variantSelections.length > 0 ? variantSelections : undefined,
// //         variantSku: selectedVariant ? sku : undefined,
// //       });
// //     }

// //     return { orderItems, totalDiscountAmount };
// //   }

// //   // ── calculateOrderTotals ──────────────────────────────────────────────────
// //   static calculateOrderTotals(
// //     items: OrderItem[],
// //     taxAmount: number,
// //     shippingCost: number,
// //     _discountAmount: number
// //   ): { subtotal: number; totalAmount: number } {
// //     const subtotal = items.reduce((sum, item) => sum + item.totalPrice, 0);
// //     const totalAmount = Math.max(0, subtotal + taxAmount + shippingCost);
// //     return { subtotal, totalAmount };
// //   }

// //   // ── uploadPaymentProof ────────────────────────────────────────────────────
// //   /**
// //    * Uploads a payment proof file (image or PDF) to Firebase Storage.
// //    * Path: order_payment_proofs/{orderId}/{timestamp}_{filename}
// //    *
// //    * Returns the public download URL, detected type, and original filename.
// //    */
// //   static async uploadPaymentProof(
// //     file: File,
// //     orderId: string,
// //     onProgress?: (percent: number) => void
// //   ): Promise<{ url: string; type: 'image' | 'pdf'; fileName: string }> {
// //     const timestamp = Date.now();
// //     const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
// //     const storagePath = `order_payment_proofs/${orderId}/${timestamp}_${safeFileName}`;
// //     const storageRef = ref(storage, storagePath);

// //     return new Promise((resolve, reject) => {
// //       const uploadTask = uploadBytesResumable(storageRef, file, {
// //         contentType: file.type,
// //       });

// //       uploadTask.on(
// //         'state_changed',
// //         (snapshot) => {
// //           if (onProgress) {
// //             const percent = Math.round(
// //               (snapshot.bytesTransferred / snapshot.totalBytes) * 100
// //             );
// //             onProgress(percent);
// //           }
// //         },
// //         (error) => {
// //           console.error('Payment proof upload error:', error);
// //           reject(new Error(`Upload failed: ${error.message}`));
// //         },
// //         async () => {
// //           try {
// //             const url = await getDownloadURL(uploadTask.snapshot.ref);
// //             const type: 'image' | 'pdf' =
// //               file.type === 'application/pdf' ? 'pdf' : 'image';
// //             resolve({ url, type, fileName: file.name });
// //           } catch (err) {
// //             reject(new Error('Failed to get download URL after upload'));
// //           }
// //         }
// //       );
// //     });
// //   }

// //   // ── deletePaymentProof ────────────────────────────────────────────────────
// //   /**
// //    * Deletes a payment proof file from Firebase Storage using its download URL.
// //    * Silently succeeds if the file no longer exists.
// //    */
// //   static async deletePaymentProof(downloadUrl: string): Promise<void> {
// //     try {
// //       const storageRef = ref(storage, downloadUrl);
// //       await deleteObject(storageRef);
// //     } catch (error: any) {
// //       if (error?.code === 'storage/object-not-found') return;
// //       console.error('Error deleting payment proof:', error);
// //       throw new Error(`Failed to delete payment proof: ${error.message}`);
// //     }
// //   }

// //   // ── savePaymentProofToOrder ───────────────────────────────────────────────
// //   /**
// //    * Persists payment proof metadata (url, type, fileName) onto an existing
// //    * order document. Called after a successful upload.
// //    */
// //   static async savePaymentProofToOrder(
// //     orderId: string,
// //     proofData: { url: string; type: 'image' | 'pdf'; fileName: string } | null
// //   ): Promise<void> {
// //     try {
// //       const orderRef = doc(db, ORDERS_COLLECTION, orderId);
// //       await updateDoc(orderRef, {
// //         paymentProofUrl: proofData?.url ?? null,
// //         paymentProofType: proofData?.type ?? null,
// //         paymentProofFileName: proofData?.fileName ?? null,
// //         updatedAt: serverTimestamp(),
// //       });
// //     } catch (error) {
// //       console.error('Error saving payment proof to order:', error);
// //       throw error;
// //     }
// //   }

// //   // ── createOrder ───────────────────────────────────────────────────────────
// //   static async createOrder(
// //     formData: OrderFormData
// //   ): Promise<{ success: boolean; orderId?: string; message?: string }> {
// //     try {
// //       const orderNumber = await this.generateOrderNumber();

// //       const { orderItems, totalDiscountAmount } = await this.validateOrderItems(formData.items);

// //       const taxAmount = parseFloat(formData.taxAmount) || 0;
// //       const shippingCost = parseFloat(formData.shippingCost) || 0;

// //       const passedDiscount = parseFloat(formData.discountAmount) || 0;
// //       const discountAmount = passedDiscount > 0 ? passedDiscount : totalDiscountAmount;

// //       const { subtotal, totalAmount } = this.calculateOrderTotals(
// //         orderItems,
// //         taxAmount,
// //         shippingCost,
// //         discountAmount
// //       );

// //       const orderData: any = {
// //         orderNumber,
// //         customerName: formData.customerName,
// //         customerEmail: formData.customerEmail,
// //         customerPhone: formData.customerPhone || null,
// //         customerWhatsApp: formData.customerWhatsApp,
// //         items: orderItems.map(item => ({
// //           productId: item.productId,
// //           productName: item.productName,
// //           productSku: item.productSku,
// //           productImage: item.productImage || null,
// //           quantity: item.quantity,
// //           unitPrice: item.unitPrice,
// //           discountedUnitPrice: item.discountedUnitPrice,
// //           discountPercentage: item.discountPercentage,
// //           totalPrice: item.totalPrice,
// //           variantId: item.variantId || null,
// //           variantSelections: item.variantSelections || null,
// //           variantSku: item.variantSku || null,
// //           sourceWarehouseId: null,
// //           sourceWarehouseName: null,
// //         })),
// //         subtotal,
// //         taxAmount,
// //         shippingCost,
// //         discountAmount,
// //         totalAmount,
// //         status: 'pending',
// //         paymentStatus: 'pending',
// //         paymentMethod: formData.paymentMethod,
// //         shippingAddress: formData.shippingAddress,
// //         billingAddress: formData.billingAddress || formData.shippingAddress,
// //         notes: formData.notes || null,
// //         trackingNumber: null,
// //         estimatedDelivery: null,
// //         orderDate: new Date(),
// //         updatedAt: new Date(),
// //         createdBy: null,
// //         isWarehouseAllocated: false,
// //         allocatedWarehouseId: null,
// //         allocatedWarehouseName: null,
// //         cancellationReason: null,
// //         returnReason: null,
// //         damagedItems: null,
// //         // Payment proof — always null at creation; uploaded after order is created
// //         paymentProofUrl: null,
// //         paymentProofType: null,
// //         paymentProofFileName: null,
// //       };

// //       const cleanedOrderData = cleanForFirestore(orderData);

// //       const docRef = await addDoc(collection(db, ORDERS_COLLECTION), {
// //         ...cleanedOrderData,
// //         orderDate: serverTimestamp(),
// //         updatedAt: serverTimestamp(),
// //       });

// //       return {
// //         success: true,
// //         orderId: docRef.id,
// //         message: 'Order placed successfully',
// //       };
// //     } catch (error) {
// //       console.error('Error creating order:', error);
// //       if (error instanceof Error) {
// //         return { success: false, message: error.message };
// //       }
// //       return { success: false, message: 'Failed to create order. Please try again.' };
// //     }
// //   }
// // }


// // // // src/Services/checkoutService.ts
// // // import {
// // //   collection,
// // //   addDoc,
// // //   serverTimestamp,
// // //   Timestamp,
// // //   doc,
// // //   getDoc,
// // // } from 'firebase/firestore';
// // // import { db } from '../Firebase/Config';

// // // const ORDERS_COLLECTION = 'orders';

// // // interface ShippingAddress {
// // //   fullName: string;
// // //   addressLine1: string;
// // //   addressLine2: string;
// // //   city: string;
// // //   state: string;
// // //   postalCode: string;
// // //   country: string;
// // //   phoneNumber: string;
// // // }

// // // interface OrderFormData {
// // //   customerName: string;
// // //   customerEmail: string;
// // //   customerPhone: string;
// // //   items: { productId: string; variantId?: string; quantity: string }[];
// // //   subtotal: string;
// // //   taxAmount: string;
// // //   shippingCost: string;
// // //   discountAmount: string;
// // //   status: 'pending';
// // //   paymentStatus: 'pending';
// // //   paymentMethod: 'credit_card' | 'paypal' | 'bank_transfer' | 'cash_on_delivery';
// // //   shippingAddress: ShippingAddress;
// // //   billingAddress: ShippingAddress;
// // //   notes: string;
// // //   trackingNumber: string;
// // //   estimatedDelivery: string;
// // //   allocatedWarehouseId?: string;
// // // }

// // // interface VariantSelection {
// // //   variantType: string;
// // //   variantValue: string;
// // // }

// // // interface OrderItem {
// // //   productId: string;
// // //   productName: string;
// // //   productSku: string;
// // //   productImage?: string;
// // //   quantity: number;
// // //   // unitPrice: original list price (before discount) — kept for invoicing/reference
// // //   unitPrice: number;
// // //   // discountedUnitPrice: price actually charged per unit
// // //   discountedUnitPrice: number;
// // //   // discount percentage applied (0 if none)
// // //   discountPercentage: number;
// // //   // totalPrice = discountedUnitPrice * quantity  (what the customer pays for this line)
// // //   totalPrice: number;
// // //   variantId?: string;
// // //   variantSelections?: VariantSelection[];
// // //   variantSku?: string;
// // // }

// // // // ── Helper: mirrors productService.calculateDiscountedPrice ──────────────────
// // // const calculateDiscountedPrice = (price: number, discount?: number): number => {
// // //   if (!discount || discount <= 0 || discount > 100) return price;
// // //   return price - (price * discount) / 100;
// // // };

// // // // Helper: clean data for Firestore (undefined → null)
// // // const cleanForFirestore = (data: any): any => {
// // //   if (data === null) return null;
// // //   if (data === undefined) return null;

// // //   if (Array.isArray(data)) {
// // //     return data.map(item => cleanForFirestore(item));
// // //   }
// // //   if (data instanceof Date) {
// // //     return Timestamp.fromDate(data);
// // //   }
// // //   if (data instanceof Timestamp) {
// // //     return data;
// // //   }
// // //   if (typeof data === 'object') {
// // //     const cleaned: any = {};
// // //     for (const [key, value] of Object.entries(data)) {
// // //       cleaned[key] = value !== undefined ? cleanForFirestore(value) : null;
// // //     }
// // //     return cleaned;
// // //   }
// // //   return data;
// // // };

// // // export class CheckoutService {

// // //   static async generateOrderNumber(): Promise<string> {
// // //     const timestamp = Date.now();
// // //     const randomSuffix = Math.random().toString(36).substring(2, 5).toUpperCase();
// // //     return `ORD-${timestamp}-${randomSuffix}`;
// // //   }

// // //   static async fetchProductById(productId: string): Promise<any | null> {
// // //     try {
// // //       const productRef = doc(db, 'products', productId);
// // //       const productSnap = await getDoc(productRef);
// // //       if (productSnap.exists()) {
// // //         return { id: productSnap.id, ...productSnap.data() };
// // //       }
// // //       return null;
// // //     } catch (error) {
// // //       console.error('Error fetching product:', error);
// // //       throw error;
// // //     }
// // //   }

// // //   // ── validateOrderItems ────────────────────────────────────────────────────
// // //   // Returns:
// // //   //   orderItems      — each item's totalPrice uses the DISCOUNTED unit price
// // //   //   totalDiscountAmount — total savings (originalPrice - discountedPrice) * qty
// // //   //                         stored as metadata; does NOT affect totalAmount calc
// // //   static async validateOrderItems(
// // //     items: { productId: string; variantId?: string; quantity: string }[]
// // //   ): Promise<{ orderItems: OrderItem[]; totalDiscountAmount: number }> {
// // //     const orderItems: OrderItem[] = [];
// // //     let totalDiscountAmount = 0;

// // //     for (const item of items) {
// // //       if (!item.productId || !item.quantity || parseInt(item.quantity) <= 0) {
// // //         throw new Error('Invalid item data');
// // //       }

// // //       const product = await this.fetchProductById(item.productId);
// // //       if (!product) {
// // //         throw new Error(`Product not found: ${item.productId}`);
// // //       }

// // //       let selectedVariant: any = null;
// // //       let originalUnitPrice: number = product.price;
// // //       let discountPercentage: number = product.discount || 0;
// // //       let sku: string = product.sku;
// // //       let variantSelections: VariantSelection[] = [];

// // //       // Handle variant products
// // //       if (product.hasVariants && product.variants && product.variants.length > 0) {
// // //         if (!item.variantId) {
// // //           throw new Error(`Variant selection required for product: ${product.name}`);
// // //         }

// // //         selectedVariant = product.variants.find(
// // //           (v: any) => v.id === item.variantId || v.sku === item.variantId
// // //         );

// // //         if (!selectedVariant) {
// // //           throw new Error(`Variant not found for product: ${product.name}`);
// // //         }

// // //         originalUnitPrice = selectedVariant.price;
// // //         // Variant discount takes precedence; fall back to product-level discount
// // //         discountPercentage =
// // //           selectedVariant.discount != null && selectedVariant.discount !== undefined
// // //             ? selectedVariant.discount
// // //             : product.discount || 0;
// // //         sku = selectedVariant.sku;
// // //         variantSelections = selectedVariant.variantSelections || [];
// // //       }

// // //       const quantity = parseInt(item.quantity);
// // //       const discountedUnitPrice = calculateDiscountedPrice(originalUnitPrice, discountPercentage);

// // //       // totalPrice is what the customer actually pays for this line
// // //       const totalPrice = discountedUnitPrice * quantity;

// // //       // Track savings separately (metadata — does not affect totalAmount)
// // //       totalDiscountAmount += (originalUnitPrice - discountedUnitPrice) * quantity;

// // //       const productImage =
// // //         product.images && product.images.length > 0 ? product.images[0] : undefined;

// // //       orderItems.push({
// // //         productId: product.id!,
// // //         productName: product.name,
// // //         productSku: sku,
// // //         productImage,
// // //         quantity,
// // //         unitPrice: originalUnitPrice,       // original list price (for display/invoice)
// // //         discountedUnitPrice,                // price charged
// // //         discountPercentage,                 // % applied
// // //         totalPrice,                         // discountedUnitPrice * qty
// // //         variantId: selectedVariant?.id || undefined,
// // //         variantSelections: variantSelections.length > 0 ? variantSelections : undefined,
// // //         variantSku: selectedVariant ? sku : undefined,
// // //       });
// // //     }

// // //     return { orderItems, totalDiscountAmount };
// // //   }

// // //   // ── calculateOrderTotals ──────────────────────────────────────────────────
// // //   // IMPORTANT: subtotal here is the SUM OF DISCOUNTED line totals, i.e. the
// // //   // customer's actual spend before tax and shipping.  Discount has already been
// // //   // applied at the line-item level, so we must NOT subtract it again here.
// // //   //
// // //   // totalAmount = subtotal + taxAmount + shippingCost
// // //   //
// // //   // discountAmount is stored on the order purely as metadata so that the admin
// // //   // panel and order-confirmation page can show "you saved X".
// // //   static calculateOrderTotals(
// // //     items: OrderItem[],
// // //     taxAmount: number,
// // //     shippingCost: number,
// // //     // discountAmount is ignored in the arithmetic — kept only for the return value
// // //     _discountAmount: number
// // //   ): { subtotal: number; totalAmount: number } {
// // //     const subtotal = items.reduce((sum, item) => sum + item.totalPrice, 0);
// // //     // Total = subtotal (already discounted) + tax + shipping
// // //     const totalAmount = Math.max(0, subtotal + taxAmount + shippingCost);
// // //     return { subtotal, totalAmount };
// // //   }

// // //   // ── createOrder ───────────────────────────────────────────────────────────
// // //   static async createOrder(
// // //     formData: OrderFormData
// // //   ): Promise<{ success: boolean; orderId?: string; message?: string }> {
// // //     try {
// // //       const orderNumber = await this.generateOrderNumber();

// // //       // Validate items and apply discounts at line-item level
// // //       const { orderItems, totalDiscountAmount } = await this.validateOrderItems(formData.items);

// // //       const taxAmount    = parseFloat(formData.taxAmount)    || 0;
// // //       const shippingCost = parseFloat(formData.shippingCost) || 0;

// // //       // discountAmount is metadata: prefer caller value if set, else use computed
// // //       const passedDiscount  = parseFloat(formData.discountAmount) || 0;
// // //       const discountAmount  = passedDiscount > 0 ? passedDiscount : totalDiscountAmount;

// // //       // totalAmount does NOT subtract discount because subtotal is already net
// // //       const { subtotal, totalAmount } = this.calculateOrderTotals(
// // //         orderItems,
// // //         taxAmount,
// // //         shippingCost,
// // //         discountAmount  // passed through but not subtracted
// // //       );

// // //       const orderData: any = {
// // //         orderNumber,
// // //         customerName:  formData.customerName,
// // //         customerEmail: formData.customerEmail,
// // //         customerPhone: formData.customerPhone || null,
// // //         items: orderItems.map(item => ({
// // //           productId:           item.productId,
// // //           productName:         item.productName,
// // //           productSku:          item.productSku,
// // //           productImage:        item.productImage || null,
// // //           quantity:            item.quantity,
// // //           unitPrice:           item.unitPrice,           // original list price
// // //           discountedUnitPrice: item.discountedUnitPrice, // price charged
// // //           discountPercentage:  item.discountPercentage,  // % applied
// // //           totalPrice:          item.totalPrice,          // discountedUnitPrice * qty
// // //           variantId:           item.variantId           || null,
// // //           variantSelections:   item.variantSelections   || null,
// // //           variantSku:          item.variantSku          || null,
// // //           sourceWarehouseId:   null,
// // //           sourceWarehouseName: null,
// // //         })),
// // //         subtotal,       // sum of discounted line totals
// // //         taxAmount,
// // //         shippingCost,
// // //         discountAmount, // metadata: total savings shown to customer
// // //         totalAmount,    // subtotal + tax + shipping  (discount already in subtotal)
// // //         status:        'pending',
// // //         paymentStatus: 'pending',
// // //         paymentMethod: formData.paymentMethod,
// // //         shippingAddress: formData.shippingAddress,
// // //         billingAddress:  formData.billingAddress || formData.shippingAddress,
// // //         notes:          formData.notes          || null,
// // //         trackingNumber: null,
// // //         estimatedDelivery: null,
// // //         orderDate:   new Date(),
// // //         updatedAt:   new Date(),
// // //         createdBy:   null,
// // //         isWarehouseAllocated: false,
// // //         allocatedWarehouseId:   null,
// // //         allocatedWarehouseName: null,
// // //         cancellationReason: null,
// // //         returnReason:       null,
// // //         damagedItems:       null,
// // //       };

// // //       const cleanedOrderData = cleanForFirestore(orderData);

// // //       const docRef = await addDoc(collection(db, ORDERS_COLLECTION), {
// // //         ...cleanedOrderData,
// // //         orderDate:  serverTimestamp(),
// // //         updatedAt:  serverTimestamp(),
// // //       });

// // //       return {
// // //         success: true,
// // //         orderId: docRef.id,
// // //         message: 'Order placed successfully',
// // //       };
// // //     } catch (error) {
// // //       console.error('Error creating order:', error);
// // //       if (error instanceof Error) {
// // //         return { success: false, message: error.message };
// // //       }
// // //       return { success: false, message: 'Failed to create order. Please try again.' };
// // //     }
// // //   }
// // // }